import { useEffect, useState } from 'react';
import {
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Row,
	Col,
	Badge,
	Table,
	Collapse,
	Button,
} from 'reactstrap';
import { Btn, Spinner, H6 } from 'AbstractElements';
import { useDispatch, useSelector } from 'react-redux';
import { clearDetail } from 'Features/Learning/ToolLeads/toolLeadSlice';
import { fetchToolLeadDetail } from 'Features/Learning/ToolLeads/Utils/ToolLeadUtils';
import {
	TOOL_LABELS,
	formatDateTime,
	formatWhatsappDisplay,
	buildWhatsappLink,
} from './ToolLeadUtils';
import {
	PROFILE_LABELS,
	formatPercent,
	interviewDimLabel,
	interviewLevelLabel,
	loveLanguageLabel,
	resolveInterviewPriority,
	skdLevelLabel,
	skdSectionLabel,
} from './toolLeadResultLabels';

const Field = ({ label, children }) => (
	<p className="mb-1">
		<strong>{label}:</strong> {children}
	</p>
);

const RawDataToggle = ({ data }) => {
	const [open, setOpen] = useState(false);
	return (
		<div className="mt-3">
			<Button color="link" className="p-0" onClick={() => setOpen((prev) => !prev)}>
				{open ? 'Sembunyikan data mentah' : 'Lihat data mentah (JSON)'}
			</Button>
			<Collapse isOpen={open}>
				<pre
					className="mt-2 mb-0"
					style={{
						whiteSpace: 'pre-wrap',
						wordBreak: 'break-word',
						background: '#f8f9fa',
						padding: '12px',
						borderRadius: '6px',
						maxHeight: '280px',
						overflow: 'auto',
						fontSize: '12px',
					}}
				>
					{JSON.stringify(data ?? {}, null, 2)}
				</pre>
			</Collapse>
		</div>
	);
};

const SkdResult = ({ data }) => {
	const result = data?.result || {};
	const profile = data?.profile || {};
	const pct = result.pct && typeof result.pct === 'object' ? result.pct : null;
	const red = Array.isArray(result.red) ? result.red : [];

	return (
		<div>
			<H6 attrH6={{ className: 'mb-2' }}>Hasil SKD Readiness</H6>
			<Field label="Level">
				<Badge color="info">{skdLevelLabel(result.lv)}</Badge>
				{result.lv ? <span className="text-muted ms-1">({result.lv})</span> : null}
			</Field>
			<Field label="Skor total">{formatPercent(result.total)}</Field>
			<Field label="Nomor pass">{data?.pass_no || '-'}</Field>
			{result.capped ? (
				<p className="mb-1 text-warning">
					Level dibatasi karena ada subtes di bawah passing grade.
				</p>
			) : null}

			{pct && (
				<>
					<H6 attrH6={{ className: 'mt-3 mb-2' }}>Skor per bagian</H6>
					<Table size="sm" bordered responsive>
						<thead>
							<tr>
								<th>Bagian</th>
								<th>Skor</th>
								<th>Status</th>
							</tr>
						</thead>
						<tbody>
							{Object.entries(pct).map(([key, value]) => (
								<tr key={key}>
									<td>{skdSectionLabel(key)}</td>
									<td>{formatPercent(value)}</td>
									<td>
										{red.includes(key) ? (
											<Badge color="danger">Di bawah PG</Badge>
										) : (
											<Badge color="success">OK</Badge>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</Table>
				</>
			)}

			{result.weak && (
				<Field label="Area terlemah">{skdSectionLabel(result.weak)}</Field>
			)}

			{profile && Object.keys(profile).length > 0 && (
				<>
					<H6 attrH6={{ className: 'mt-3 mb-2' }}>Profil peserta</H6>
					<ul className="mb-0">
						{Object.entries(profile).map(([key, value]) => (
							<li key={key}>
								{PROFILE_LABELS[key] || key}:{' '}
								{typeof value === 'object' ? JSON.stringify(value) : String(value ?? '-')}
							</li>
						))}
					</ul>
				</>
			)}

			{data?.time_summary && (
				<>
					<H6 attrH6={{ className: 'mt-3 mb-2' }}>Ringkasan waktu pengerjaan</H6>
					<pre
						className="mb-0"
						style={{
							whiteSpace: 'pre-wrap',
							background: '#f8f9fa',
							padding: '10px',
							borderRadius: '6px',
							fontSize: '12px',
						}}
					>
						{JSON.stringify(data.time_summary, null, 2)}
					</pre>
				</>
			)}

			<RawDataToggle data={data} />
		</div>
	);
};

const InterviewResult = ({ data }) => {
	const result = data?.result || {};
	const answers = Array.isArray(data?.answers) ? data.answers : [];
	const dims = Array.isArray(result.dims) ? result.dims : [];
	const prio = Array.isArray(result.prio) ? result.prio : [];

	return (
		<div>
			<H6 attrH6={{ className: 'mb-2' }}>Hasil Interview Readiness</H6>
			<Field label="Level kesiapan">
				<Badge color="info">{interviewLevelLabel(result.lv)}</Badge>
				{result.lv ? <span className="text-muted ms-1">({result.lv})</span> : null}
			</Field>
			<Field label="Skor total">{formatPercent(result.total)}</Field>
			<Field label="Aspek terkuat">{interviewDimLabel(result.strong)}</Field>
			<Field label="Aspek terlemah">{interviewDimLabel(result.weak)}</Field>

			{dims.length > 0 && (
				<>
					<H6 attrH6={{ className: 'mt-3 mb-2' }}>Skor per aspek</H6>
					<p className="text-muted small mb-2">
						Lima aspek penilaian interview. Angka = persen kesiapan di aspek itu.
					</p>
					<Table size="sm" bordered responsive>
						<thead>
							<tr>
								<th>Kode</th>
								<th>Aspek</th>
								<th>Skor</th>
							</tr>
						</thead>
						<tbody>
							{dims.map((dim) => (
								<tr key={dim?.id || dim?.name}>
									<td>{dim?.id || '-'}</td>
									<td>{interviewDimLabel(dim?.id) || dim?.name || '-'}</td>
									<td>{formatPercent(dim?.p ?? dim?.pct)}</td>
								</tr>
							))}
						</tbody>
					</Table>
				</>
			)}

			{prio.length > 0 && (
				<>
					<H6 attrH6={{ className: 'mt-3 mb-2' }}>3 prioritas latihan</H6>
					<p className="text-muted small mb-2">
						Bukan tingkat urgensi tiket. Ini 3 pernyataan dengan jawaban paling lemah yang
						disarankan dilatih dulu.
					</p>
					{prio.map((questionIndex, order) => {
						const item = resolveInterviewPriority(questionIndex, answers);
						return (
							<div
								key={`prio-${questionIndex}-${order}`}
								className="border rounded p-2 mb-2"
								style={{ background: '#fafafa' }}
							>
								<p className="mb-1">
									<strong>
										Prioritas {order + 1}
									</strong>{' '}
									<span className="text-muted">({item.orderLabel})</span>
								</p>
								<p className="mb-1">
									<Badge color="secondary">{item.dimLabel}</Badge>
								</p>
								<p className="mb-1">
									<strong>Saran latihan:</strong> {item.tip || '-'}
								</p>
								<p className="mb-1 small text-muted">Pernyataan: {item.statement}</p>
								<p className="mb-0 small">
									Jawaban peserta: <Badge color="light">{item.answerLabel}</Badge>
								</p>
							</div>
						);
					})}
				</>
			)}

			<RawDataToggle data={data} />
		</div>
	);
};

const LoveLanguageResult = ({ data }) => {
	const result = data?.result || {};
	const sorted = Array.isArray(result.sorted) ? result.sorted : [];

	return (
		<div>
			<H6 attrH6={{ className: 'mb-2' }}>Hasil Tes Love Language</H6>
			{result.main && (
				<Field label="Bahasa utama">
					<Badge color="primary">{loveLanguageLabel(result.main)}</Badge>
				</Field>
			)}
			{sorted.length > 0 ? (
				<>
					<H6 attrH6={{ className: 'mt-3 mb-2' }}>Peringkat</H6>
					<Table size="sm" bordered responsive>
						<thead>
							<tr>
								<th>#</th>
								<th>Bahasa cinta</th>
								<th>Jumlah</th>
								<th>Persen</th>
							</tr>
						</thead>
						<tbody>
							{sorted.map((item, index) => (
								<tr key={`${item?.key || index}-${index}`}>
									<td>{index + 1}</td>
									<td>{loveLanguageLabel(item?.key || item?.name)}</td>
									<td>{item?.count ?? item?.score ?? '-'}</td>
									<td>{formatPercent(item?.pct ?? item?.value)}</td>
								</tr>
							))}
						</tbody>
					</Table>
				</>
			) : (
				<p className="text-muted">Tidak ada peringkat bahasa cinta.</p>
			)}
			<RawDataToggle data={data} />
		</div>
	);
};

const CvBuilderResult = ({ data }) => {
	const result = data?.result || {};
	const cv = data?.cv || {};

	return (
		<div>
			<H6 attrH6={{ className: 'mb-2' }}>Hasil CV Builder</H6>
			<Field label="Skor ATS">{result.total != null ? String(result.total) : '-'}</Field>
			<Field label="Level">{result.lv || '-'}</Field>
			{result.summary && <Field label="Ringkasan">{String(result.summary)}</Field>}

			{Array.isArray(result.strengths) && result.strengths.length > 0 && (
				<>
					<H6 attrH6={{ className: 'mt-3 mb-1' }}>Kekuatan</H6>
					<ul>
						{result.strengths.map((item, index) => (
							<li key={`strength-${index}`}>{String(item)}</li>
						))}
					</ul>
				</>
			)}
			{Array.isArray(result.gaps) && result.gaps.length > 0 && (
				<>
					<H6 attrH6={{ className: 'mt-2 mb-1' }}>Celah / kekurangan</H6>
					<ul>
						{result.gaps.map((item, index) => (
							<li key={`gap-${index}`}>{String(item)}</li>
						))}
					</ul>
				</>
			)}
			{Array.isArray(result.changes) && result.changes.length > 0 && (
				<>
					<H6 attrH6={{ className: 'mt-2 mb-1' }}>Saran perubahan</H6>
					<ul>
						{result.changes.map((item, index) => (
							<li key={`change-${index}`}>{String(item)}</li>
						))}
					</ul>
				</>
			)}

			{(cv.title || cv.summary || cv.template) && (
				<>
					<H6 attrH6={{ className: 'mt-3 mb-2' }}>Ringkasan CV</H6>
					{cv.title && <Field label="Judul">{cv.title}</Field>}
					{cv.template && <Field label="Template">{cv.template}</Field>}
					{cv.summary && <Field label="Summary">{cv.summary}</Field>}
					{cv.linkedin && <Field label="LinkedIn">{cv.linkedin}</Field>}
					{Array.isArray(cv.experience) && (
						<Field label="Pengalaman">{cv.experience.length} entri</Field>
					)}
					{Array.isArray(cv.education) && (
						<Field label="Pendidikan">{cv.education.length} entri</Field>
					)}
				</>
			)}

			<RawDataToggle data={data} />
		</div>
	);
};

const KepribadianResult = ({ data }) => {
	const result = data?.result || {};
	const bars = Array.isArray(result.bars) ? result.bars : [];

	return (
		<div>
			<H6 attrH6={{ className: 'mb-2' }}>Hasil Tes Kepribadian</H6>
			<Field label="Tipe">{result.type || '-'}</Field>
			{bars.length > 0 && (
				<>
					<H6 attrH6={{ className: 'mt-3 mb-2' }}>Dimensi</H6>
					<Table size="sm" bordered responsive>
						<thead>
							<tr>
								<th>Sisi A</th>
								<th>Sisi B</th>
								<th>% A</th>
								<th>% B</th>
								<th>Dominan</th>
							</tr>
						</thead>
						<tbody>
							{bars.map((bar, index) => (
								<tr key={`bar-${index}`}>
									<td>{bar?.a || '-'}</td>
									<td>{bar?.b || '-'}</td>
									<td>{formatPercent(bar?.pct_a ?? bar?.pctA)}</td>
									<td>{formatPercent(bar?.pct_b ?? bar?.pctB)}</td>
									<td>{bar?.win || '-'}</td>
								</tr>
							))}
						</tbody>
					</Table>
				</>
			)}
			<RawDataToggle data={data} />
		</div>
	);
};

const GenericResult = ({ data }) => (
	<div>
		<p className="text-muted mb-2">
			Format hasil tool ini belum punya tampilan khusus. Data mentah ditampilkan di bawah.
		</p>
		<pre
			style={{
				whiteSpace: 'pre-wrap',
				wordBreak: 'break-word',
				background: '#f8f9fa',
				padding: '12px',
				borderRadius: '6px',
				maxHeight: '360px',
				overflow: 'auto',
				fontSize: '12px',
			}}
		>
			{JSON.stringify(data ?? {}, null, 2)}
		</pre>
	</div>
);

const renderToolData = (toolsName, data) => {
	if (!data) {
		return <p className="text-muted mb-0">Tidak ada data hasil.</p>;
	}

	switch (toolsName) {
		case 'skd-readiness':
			return <SkdResult data={data} />;
		case 'interview-readiness':
			return <InterviewResult data={data} />;
		case 'tes-love-language':
			return <LoveLanguageResult data={data} />;
		case 'cv-builder':
			return <CvBuilderResult data={data} />;
		case 'tes-kepribadian':
			return <KepribadianResult data={data} />;
		default:
			return <GenericResult data={data} />;
	}
};

const ModalDetailToolLead = ({ isOpen, toggle, leadId }) => {
	const dispatch = useDispatch();
	const detail = useSelector((state) => state.ToolLead.detail);
	const isDetailLoading = useSelector((state) => state.ToolLead.isDetailLoading);

	useEffect(() => {
		if (isOpen && leadId) {
			dispatch(fetchToolLeadDetail(leadId));
			return;
		}
		dispatch(clearDetail());
	}, [dispatch, isOpen, leadId]);

	const handleClose = () => {
		dispatch(clearDetail());
		toggle();
	};

	const waLink = detail ? buildWhatsappLink(detail.whatsapp, detail.name) : null;

	return (
		<Modal isOpen={isOpen} toggle={handleClose} size="lg">
			<ModalHeader toggle={handleClose}>Detail Tool Lead</ModalHeader>
			<ModalBody>
				{isDetailLoading ? (
					<div className="text-center py-4">
						<Spinner attrSpinner={{ className: 'loader-30' }} />
						<p className="mt-2 mb-0">Memuat detail...</p>
					</div>
				) : !detail ? (
					<p className="text-muted mb-0">Data lead tidak ditemukan.</p>
				) : (
					<>
						<Row className="mb-3">
							<Col md="6">
								<Field label="Nama">{detail.name || '-'}</Field>
								<Field label="Email">{detail.email || '-'}</Field>
								<Field label="WhatsApp">
									{waLink ? (
										<a href={waLink} target="_blank" rel="noreferrer">
											{formatWhatsappDisplay(detail.whatsapp)}
										</a>
									) : (
										'-'
									)}
								</Field>
								<Field label="Tools">
									<Badge color="secondary">
										{TOOL_LABELS[detail.tools_name] || detail.tools_name}
									</Badge>
								</Field>
							</Col>
							<Col md="6">
								<Field label="Percobaan">{detail.attempts ?? '-'}</Field>
								<Field label="Consent">{formatDateTime(detail.consent_at)}</Field>
								<Field label="Versi Consent">{detail.consent_version || '-'}</Field>
								<Field label="IP">{detail.ip_address || '-'}</Field>
								<Field label="Daftar">{formatDateTime(detail.created_at)}</Field>
								<Field label="Update">{formatDateTime(detail.updated_at)}</Field>
							</Col>
						</Row>
						<hr />
						{renderToolData(detail.tools_name, detail.data)}
					</>
				)}
			</ModalBody>
			<ModalFooter>
				<Btn attrBtn={{ color: 'secondary', onClick: handleClose }}>Tutup</Btn>
			</ModalFooter>
		</Modal>
	);
};

export default ModalDetailToolLead;
